import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  History,
  Loader2,
  MapPin,
  Navigation,
  Radio,
  Route,
  ShieldAlert,
  Truck,
  Users,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";

type EquipeOpt = { id: number; nom: string; zone?: { nom?: string | null } | null };
type AgentRow = {
  id: number;
  nom: string;
  prenom: string;
  fonction?: string | null;
  actif?: boolean;
  equipeId?: number | null;
  disponibilite?: string | null;
};

type DispatchBadge =
  | "ONLINE"
  | "EN_TOURNEE"
  | "GPS_LIVE"
  | "SATURE"
  | "SLA_RISQUE"
  | "HORS_ZONE"
  | "VEHICULE_OFFLINE";

type DispatchContext = {
  id: number;
  client: string;
  zone: string | null;
  priorite: "NORMALE" | "ELEVEE" | "CRITIQUE";
  typePrestation: string;
  creneau: string;
  sla: "OK" | "RETARD" | "CRITIQUE" | "INCONNU";
  urgence: boolean;
  statutProvision: string;
  incidentsHistorique: number;
  retardMinutes: number;
};

type DispatchEquipe = {
  id: number;
  nom: string;
  recommended: boolean;
  recommendationScore: number;
  recommendationReasons: string[];
  blockers: { code: string; message: string }[];
  canAssign: boolean;
  badges: DispatchBadge[];
  agentsActifs: number;
  vehicle: { id: number; label: string; status: string | null } | null;
  chauffeur: { id: number; label: string } | null;
  zoneActuelle: string | null;
  chargeActuelle: number;
  capaciteMax: number;
  chargeApresAffectation: number;
  passagesRestants: number;
  slaRisk: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  distanceGpsEstimeeM: number | null;
  tourneeActive: { id: number; numero: string | null; statut: string } | null;
  dernierSignalGps: string | null;
  statutLive: "ONLINE" | "EN_TOURNEE" | "HORS_LIGNE" | "SATURE" | "BLOQUE";
};

type DispatchCenter = {
  generatedAt: string;
  realtime: {
    channel: "SSE";
    endpoint: string;
    preparedEvents: string[];
  };
  passage: DispatchContext | null;
  recommendedEquipeId: number | null;
  equipes: DispatchEquipe[];
};

export type AffecterAgentsEquipeDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  equipes: EquipeOpt[];
  initialEquipeId?: number | null;
  passageId?: number | null;
};

export default function AffecterAgentsEquipeDrawer({
  open,
  onClose,
  onSaved,
  equipes,
  initialEquipeId = null,
  passageId = null,
}: AffecterAgentsEquipeDrawerProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const [equipeId, setEquipeId] = useState<number | "">("");
  const [dispatch, setDispatch] = useState<DispatchCenter | null>(null);
  const [libres, setLibres] = useState<AgentRow[]>([]);
  const [membres, setMembres] = useState<AgentRow[]>([]);
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pickIds, setPickIds] = useState<number[]>([]);

  const selectedEquipe = useMemo(
    () => dispatch?.equipes.find((e) => e.id === equipeId) ?? null,
    [dispatch?.equipes, equipeId]
  );

  const loadDispatch = useCallback(async () => {
    setDispatchLoading(true);
    try {
      const res = await api.get<DispatchCenter>("/equipes/dispatch/center", {
        params: passageId ? { passageId } : undefined,
      });
      const next = res.data;
      setDispatch(next);
      const preferred = initialEquipeId ?? next.recommendedEquipeId ?? next.equipes[0]?.id ?? null;
      if (preferred != null) setEquipeId(preferred);
    } catch {
      toast.error("Impossible de charger le dispatch opérationnel.", {
        toastId: "org-dispatch-load",
      });
      setDispatch(null);
    } finally {
      setDispatchLoading(false);
    }
  }, [initialEquipeId, passageId]);

  const loadLibres = useCallback(async () => {
    try {
      const res = await api.get("/equipes/libres");
      const raw = Array.isArray(res.data) ? res.data : [];
      setLibres(raw);
    } catch {
      toast.error("Impossible de charger les agents sans équipe.", {
        toastId: "org-terrain-libres",
      });
      setLibres([]);
    }
  }, []);

  const loadMembres = useCallback(async (eid: number) => {
    setLoading(true);
    try {
      const res = await api.get(`/equipes/${eid}`);
      const eq = (res.data as { equipe?: { agents?: AgentRow[] } }).equipe;
      const raw = Array.isArray(eq?.agents) ? eq!.agents! : [];
      setMembres(raw);
      setPickIds(raw.map((a) => a.id));
    } catch {
      toast.error("Impossible de charger l’équipe.", { toastId: "org-terrain-eq-load" });
      setMembres([]);
      setPickIds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setEquipeId("");
      setDispatch(null);
      setMembres([]);
      setPickIds([]);
      return;
    }
    void loadDispatch();
    void loadLibres();
  }, [open, loadDispatch, loadLibres]);

  useEffect(() => {
    if (!open || equipeId === "") return;
    void loadMembres(Number(equipeId));
  }, [equipeId, open, loadMembres]);

  const toggleMembre = (id: number, on: boolean) => {
    setPickIds((prev) => (on ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)));
  };

  const removeAgent = (id: number) => {
    setPickIds((prev) => prev.filter((x) => x !== id));
  };

  const addLibre = (a: AgentRow) => {
    setPickIds((prev) => [...new Set([...prev, a.id])]);
  };

  const save = async () => {
    if (equipeId === "") {
      toast.warning("Choisissez une équipe opérationnelle.");
      return;
    }
    if (selectedEquipe && !selectedEquipe.canAssign) {
      toast.error(selectedEquipe.blockers[0]?.message ?? "Affectation bloquée par le dispatch.");
      return;
    }
    setSaving(true);
    try {
      if (passageId != null) {
        await api.post("/equipes/dispatch/assign", { passageId, equipeId: Number(equipeId) });
      } else {
        await api.patch(`/equipes/${Number(equipeId)}/agents`, { agentIds: pickIds });
      }
      toast.success("Affectation opérationnelle enregistrée.", {
        toastId: "org-terrain-agents-save",
      });
      onSaved();
      onClose();
      void loadLibres();
    } catch {
      toast.error("Impossible d’affecter cet agent.", { toastId: "org-terrain-agents-err" });
    } finally {
      setSaving(false);
    }
  };

  const libresFiltered = useMemo(() => {
    return libres.filter((a) => !pickIds.includes(a.id));
  }, [libres, pickIds]);

  const membresEffectifs = useMemo(() => {
    const map = new Map<number, AgentRow>();
    membres.forEach((a) => map.set(a.id, a));
    libres.forEach((a) => map.set(a.id, a));
    return pickIds
      .map((id) => map.get(id))
      .filter((a): a is AgentRow => a != null);
  }, [membres, libres, pickIds]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-black/60 backdrop-blur-sm">
      <div
        className="flex h-full w-full max-w-6xl flex-col border-l border-slate-800 bg-slate-950 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-agents-title"
      >
        <div className="sticky top-0 z-20 border-b border-slate-800 bg-slate-950/95 p-4 backdrop-blur">
          <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="drawer-agents-title" className="text-lg font-semibold text-white">
              Centre de dispatch opérationnel
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Cartes équipes, capacité live, recommandation backend et contrôles métier.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
          </div>

          <DispatchContextHeader context={dispatch?.passage ?? null} loading={dispatchLoading} />
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2 text-xs text-slate-400">
            <span className="inline-flex items-center gap-2">
              <Radio className="h-3.5 w-3.5 text-emerald-400" />
              Temps réel préparé : {dispatch?.realtime.preparedEvents.join(" · ") ?? "SSE en attente"}
            </span>
            <button
              type="button"
              onClick={() => void loadDispatch()}
              className="rounded-lg border border-slate-700 px-2 py-1 text-slate-300 hover:bg-slate-800"
            >
              Rafraîchir
            </button>
          </div>

          {dispatchLoading ? (
            <DispatchSkeleton />
          ) : (
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1.55fr)_minmax(360px,0.95fr)]">
              <section className="min-w-0">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Équipes opérationnelles
                  </h3>
                  <span className="text-[11px] text-slate-600">
                    {dispatch?.equipes.length ?? equipes.length} équipe(s)
                  </span>
                </div>
                <div className="grid gap-3 lg:grid-cols-2">
                  {(dispatch?.equipes ?? []).map((equipe) => (
                    <DispatchEquipeCard
                      key={equipe.id}
                      equipe={equipe}
                      selected={equipeId === equipe.id}
                      onSelect={() => setEquipeId(equipe.id)}
                    />
                  ))}
                </div>
              </section>

              <aside className="min-w-0 space-y-4">
                {selectedEquipe ? (
                  <RecommendationPanel equipe={selectedEquipe} />
                ) : (
                  <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 text-sm text-slate-500">
                    Sélectionnez une équipe pour afficher la charge prévisionnelle.
                  </div>
                )}

                {equipeId !== "" && loading ? (
                  <p className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/40 p-3 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> Chargement des agents…
                  </p>
                ) : equipeId !== "" ? (
                  <AgentAssignmentPanel
                    membres={membresEffectifs}
                    libres={libresFiltered}
                    onRemove={removeAgent}
                    onAdd={addLibre}
                  />
                ) : null}
              </aside>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 z-20 border-t border-slate-800 bg-slate-950/95 p-4 backdrop-blur">
          {selectedEquipe?.blockers.length ? (
            <div className="mb-3 rounded-xl border border-rose-900/50 bg-rose-950/30 px-3 py-2 text-xs text-rose-100">
              <p className="font-semibold">Affectation bloquée</p>
              <p className="mt-1">{selectedEquipe.blockers.map((b) => b.message).join(" · ")}</p>
            </div>
          ) : null}
          <div className="grid gap-2 sm:grid-cols-4">
          <button
            type="button"
            disabled={saving || equipeId === "" || Boolean(selectedEquipe && !selectedEquipe.canAssign)}
            onClick={() => void save()}
            className="rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
          >
            {saving ? "Enregistrement…" : "Affecter maintenant"}
          </button>
          <button
            type="button"
            disabled={!selectedEquipe?.tourneeActive}
            onClick={() => {
              if (selectedEquipe?.tourneeActive) {
                navigate(`/admin/planificateur?tourneeId=${selectedEquipe.tourneeActive.id}`);
              }
            }}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 py-2.5 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-40"
          >
            <Route className="h-4 w-4" />
            Voir tournée
          </button>
          <button
            type="button"
            disabled={equipeId === ""}
            onClick={() => navigate(`/admin/terrain/carte?equipeId=${String(equipeId)}`)}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 py-2.5 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-40"
          >
            <MapPin className="h-4 w-4" />
            Voir GPS
          </button>
          <button
            type="button"
            disabled={equipeId === ""}
            onClick={() => navigate(`/admin/equipes/${String(equipeId)}`)}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 py-2.5 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-40"
          >
            <History className="h-4 w-4" />
            Historique équipe
          </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DispatchContextHeader({
  context,
  loading,
}: {
  context: DispatchContext | null;
  loading: boolean;
}): React.JSX.Element {
  if (loading) {
    return <div className="mt-3 h-16 animate-pulse rounded-xl bg-slate-900" />;
  }
  if (!context) {
    return (
      <div className="mt-3 rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2 text-xs text-slate-400">
        <span className="font-semibold text-slate-200">Contexte global équipes.</span>{" "}
        Aucun passage ciblé : les cartes affichent la capacité live et les risques terrain du jour.
      </div>
    );
  }
  return (
    <div className="mt-3 grid gap-2 rounded-xl border border-slate-800 bg-slate-900/60 p-3 text-xs md:grid-cols-4">
      <Metric label="Client" value={context.client} />
      <Metric label="Zone" value={context.zone ?? "—"} />
      <Metric label="Priorité" value={context.priorite} tone={context.priorite === "CRITIQUE" ? "danger" : "warn"} />
      <Metric label="Retard" value={formatDelay(context.retardMinutes)} tone={context.retardMinutes > 0 ? "danger" : "ok"} />
      <Metric label="Type prestation" value={context.typePrestation} />
      <Metric label="Créneau" value={new Date(context.creneau).toLocaleString("fr-FR")} />
      <Metric label="SLA" value={context.sla} tone={context.sla === "CRITIQUE" ? "danger" : context.sla === "RETARD" ? "warn" : "ok"} />
      <Metric label="Provision / incidents" value={`${context.statutProvision} · ${context.incidentsHistorique} incident(s)`} />
    </div>
  );
}

function DispatchEquipeCard({
  equipe,
  selected,
  onSelect,
}: {
  equipe: DispatchEquipe;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`rounded-2xl border p-3 text-left transition hover:-translate-y-0.5 hover:bg-slate-900 ${
        selected
          ? "border-emerald-500/60 bg-emerald-950/20 shadow-lg shadow-emerald-950/20"
          : "border-slate-800 bg-slate-900/45"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="truncate text-sm font-semibold text-white">{equipe.nom}</h4>
            {equipe.recommended ? (
              <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold text-emerald-200">
                Recommandée
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-[11px] text-slate-500">{equipe.zoneActuelle ?? "Zone non renseignée"}</p>
        </div>
        <LiveStatus status={equipe.statutLive} />
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {equipe.badges.map((badge) => (
          <LiveBadge key={badge} badge={badge} />
        ))}
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <Metric label="Agents actifs" value={String(equipe.agentsActifs)} />
        <Metric label="Charge" value={`${equipe.chargeActuelle}/${equipe.capaciteMax || "∞"}`} />
        <Metric label="Après affectation" value={`${equipe.chargeApresAffectation}/${equipe.capaciteMax || "∞"}`} tone={equipe.canAssign ? "ok" : "danger"} />
        <Metric label="Restants" value={String(equipe.passagesRestants)} />
        <Metric label="Chauffeur" value={equipe.chauffeur?.label ?? "—"} />
        <Metric label="Véhicule" value={equipe.vehicle?.label ?? "—"} />
        <Metric label="SLA risk" value={equipe.slaRisk} tone={equipe.slaRisk === "CRITICAL" || equipe.slaRisk === "HIGH" ? "danger" : "ok"} />
        <Metric label="GPS estimé" value={equipe.distanceGpsEstimeeM != null ? `${Math.round(equipe.distanceGpsEstimeeM / 1000)} km` : "—"} />
      </dl>

      {equipe.blockers.length ? (
        <div className="mt-3 rounded-lg border border-rose-900/50 bg-rose-950/25 px-2 py-1.5 text-[11px] text-rose-100">
          {equipe.blockers.map((b) => b.message).join(" · ")}
        </div>
      ) : null}
    </button>
  );
}

function RecommendationPanel({ equipe }: { equipe: DispatchEquipe }): React.JSX.Element {
  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-400">
            Équipe recommandée
          </p>
          <h3 className="mt-1 text-base font-semibold text-white">{equipe.nom}</h3>
        </div>
        <span className="rounded-full border border-slate-700 px-2 py-0.5 text-xs text-slate-300">
          Score {equipe.recommendationScore}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 rounded-xl border border-slate-800 bg-slate-950/50 p-3 text-xs">
        <Metric label="Charge actuelle" value={`${equipe.chargeActuelle}/${equipe.capaciteMax || "∞"}`} />
        <Metric label="Après affectation" value={`${equipe.chargeApresAffectation}/${equipe.capaciteMax || "∞"}`} tone={equipe.canAssign ? "ok" : "danger"} />
      </div>
      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Raison</p>
      <ul className="mt-2 space-y-1 text-sm text-slate-300">
        {equipe.recommendationReasons.length ? (
          equipe.recommendationReasons.map((r) => (
            <li key={r} className="flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
              {r}
            </li>
          ))
        ) : (
          <li className="flex items-center gap-2 text-slate-500">
            <AlertTriangle className="h-3.5 w-3.5" />
            Aucune raison favorable calculée.
          </li>
        )}
      </ul>
    </section>
  );
}

function AgentAssignmentPanel({
  membres,
  libres,
  onRemove,
  onAdd,
}: {
  membres: AgentRow[];
  libres: AgentRow[];
  onRemove: (id: number) => void;
  onAdd: (agent: AgentRow) => void;
}): React.JSX.Element {
  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Contexte agents terrain
      </h3>
      <div className="mt-3 grid gap-3 lg:grid-cols-2 xl:grid-cols-1">
        <AgentList title="Membres de l’équipe" empty="Aucun agent sélectionné.">
          {membres.map((a) => (
            <AgentListRow key={a.id} agent={a} actionLabel="Retirer" onAction={() => onRemove(a.id)} />
          ))}
        </AgentList>
        <AgentList title="Agents sans équipe" empty="Aucun agent libre à ajouter.">
          {libres.map((a) => (
            <AgentListRow key={a.id} agent={a} actionLabel="Ajouter" onAction={() => onAdd(a)} primary />
          ))}
        </AgentList>
      </div>
    </section>
  );
}

function AgentList({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode[];
}): React.JSX.Element {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</p>
      <ul className="mt-2 max-h-56 space-y-2 overflow-y-auto pr-1">
        {children.length ? (
          children
        ) : (
          <li className="rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2 text-sm text-slate-500">
            {empty}
          </li>
        )}
      </ul>
    </div>
  );
}

function AgentListRow({
  agent,
  actionLabel,
  onAction,
  primary = false,
}: {
  agent: AgentRow;
  actionLabel: string;
  onAction: () => void;
  primary?: boolean;
}): React.JSX.Element {
  return (
    <li className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-200">
      <span className="min-w-0">
        <span className="block truncate">
          {agent.prenom} {agent.nom}
        </span>
        <span className="block truncate text-xs text-slate-500">
          {agent.fonction ?? "—"} · {agent.disponibilite ?? "—"}
        </span>
      </span>
      <button
        type="button"
        onClick={onAction}
        className={
          primary
            ? "rounded-lg bg-emerald-600/90 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-500"
            : "text-xs font-medium text-rose-400 hover:text-rose-300"
        }
      >
        {actionLabel}
      </button>
    </li>
  );
}

function DispatchSkeleton(): React.JSX.Element {
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.55fr)_minmax(360px,0.95fr)]">
      <div className="grid gap-3 lg:grid-cols-2">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div key={idx} className="h-64 animate-pulse rounded-2xl border border-slate-800 bg-slate-900" />
        ))}
      </div>
      <div className="space-y-3">
        <div className="h-44 animate-pulse rounded-2xl border border-slate-800 bg-slate-900" />
        <div className="h-72 animate-pulse rounded-2xl border border-slate-800 bg-slate-900" />
      </div>
    </div>
  );
}

function LiveBadge({ badge }: { badge: DispatchBadge }): React.JSX.Element {
  const map: Record<DispatchBadge, { label: string; cls: string }> = {
    ONLINE: { label: "ONLINE", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" },
    EN_TOURNEE: { label: "EN TOURNÉE", cls: "border-sky-500/40 bg-sky-500/10 text-sky-200" },
    GPS_LIVE: { label: "GPS LIVE", cls: "border-cyan-500/40 bg-cyan-500/10 text-cyan-200" },
    SATURE: { label: "SATURÉ", cls: "border-amber-500/40 bg-amber-500/10 text-amber-200" },
    SLA_RISQUE: { label: "SLA RISQUE", cls: "border-rose-500/40 bg-rose-500/10 text-rose-200" },
    HORS_ZONE: { label: "HORS ZONE", cls: "border-violet-500/40 bg-violet-500/10 text-violet-200" },
    VEHICULE_OFFLINE: { label: "VÉHICULE OFFLINE", cls: "border-orange-500/40 bg-orange-500/10 text-orange-200" },
  };
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${map[badge].cls}`}>
      {map[badge].label}
    </span>
  );
}

function LiveStatus({ status }: { status: DispatchEquipe["statutLive"] }): React.JSX.Element {
  const icon =
    status === "EN_TOURNEE" ? <Navigation className="h-3.5 w-3.5" /> :
    status === "BLOQUE" ? <ShieldAlert className="h-3.5 w-3.5" /> :
    status === "SATURE" ? <AlertTriangle className="h-3.5 w-3.5" /> :
    status === "ONLINE" ? <Activity className="h-3.5 w-3.5" /> :
    <Clock className="h-3.5 w-3.5" />;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-2 py-0.5 text-[10px] font-bold text-slate-300">
      {icon}
      {status}
    </span>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok" | "warn" | "danger";
}): React.JSX.Element {
  const toneCls =
    tone === "ok"
      ? "text-emerald-200"
      : tone === "warn"
        ? "text-amber-200"
        : tone === "danger"
          ? "text-rose-200"
          : "text-slate-200";
  return (
    <div className="min-w-0">
      <dt className="truncate text-[10px] uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className={`truncate font-medium ${toneCls}`}>{value}</dd>
    </div>
  );
}

function formatDelay(minutes: number): string {
  if (minutes <= 0) return "0 min";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h${m > 0 ? ` ${m}m` : ""}` : `${m} min`;
}
